import React, { useEffect, useRef, useState, useCallback, useId } from 'react';
import styles from './DataSourceSelector.module.scss';
import WebIcon from '../../../../../../../../assets/Svg/WebIcon';
import AssuredHouseIcon from '../../../../../../../../assets/Svg/AssuredHouseIcon';
import EditPencilIcon from '../../../../../../../../assets/Svg/EditPencilIcon';
import DatabaseIcon from '../../../../../../../../assets/Svg/DatabaseIcon';
import FourDotsMenuIcon from '../../../../../../../../assets/Svg/FourDotsMenuIcon';
import BulbIcon from '../../../../../../../../assets/Svg/BulbIcon';
import TickIcon from '../../../../../../../../assets/Svg/TickIcon';
import { databaseSearchIcon } from '../../../../../../../../assets/hostedassets';

interface DataSource {
    value: string;
    icon: React.ReactNode;
    iconPurple: React.ReactNode;
    label: string;
    isActive: boolean;
    description: string;
    isDisabled: boolean;
}

interface DataSourceSelectorProps {
    selectedSources: string[];
    onSelectionChange: (sources: string[]) => void;
    onClose: () => void;
    openAbove?: boolean; // New prop to control the direction of the popup
    buttonStyle?: 'collapsed' | 'input'; // New prop to control button styling
}

const DataSourceSelector: React.FC<DataSourceSelectorProps> = ({
    selectedSources,
    onSelectionChange,
    onClose,
    openAbove = false,
    buttonStyle = 'collapsed',
}) => {
    const selectorRef = useRef<HTMLDivElement>(null);
    const [hoveredSource, setHoveredSource] = useState<string | null>(null);
    const [showDropdown, setShowDropdown] = useState(false);

    // Create a stable ID for aria-controls
    const dropdownId = useId();

    // Available data sources configuration
    const dataSources: DataSource[] = [
        {
            value: 'searchTheWeb',
            icon: <WebIcon color="#000000" width="12" height="12" />,
            iconPurple: <WebIcon color="#6021B3" width="12" height="12" />,
            label: 'Search the Web',
            isActive: true,
            isDisabled: true,
            description: 'Recent news, market insights, and public web information',
        },
        {
            value: 'trustedPublicRecords',
            icon: <AssuredHouseIcon color="#000000" width="14" height="14" />,
            iconPurple: <AssuredHouseIcon color="#6021B3" width="14" height="14" />,
            label: 'Trusted Public Records',
            isActive: true,
            isDisabled: true,
            description: 'MCA filings, regulatory data, and verified public information',
        },
        {
            value: 'consentData',
            icon: <EditPencilIcon color="#000000" width="14" height="14" />,
            iconPurple: <EditPencilIcon color="#6021B3" width="14" height="14" />,
            label: 'Consent Data',
            isActive: true,
            isDisabled: true,
            description: 'Consent data from the user',
        },
        {
            value: 'dataRoom',
            icon: <DatabaseIcon color="#000000" width="14" height="14" />,
            iconPurple: <DatabaseIcon color="#6021B3" width="14" height="14" />,
            label: 'Data Room',
            isActive: true,
            isDisabled: true,
            description: 'Data from the user',
        },
        {
            value: 'dealContext',
            icon: <FourDotsMenuIcon color="#000000" width="14" height="14" />,
            iconPurple: <FourDotsMenuIcon color="#6021B3" width="14" height="14" />,
            label: 'Deal Context',
            isActive: true,
            isDisabled: true,
            description: 'Deal context from the user',
        },
        {
            value: 'portfolioIntelligence',
            icon: <BulbIcon color="#000000" width="14" height="14" />,
            iconPurple: <BulbIcon color="#6021B3" width="14" height="14" />,
            label: 'Portfolio Intelligence',
            isActive: true,
            isDisabled: true,
            description: 'Portfolio intelligence from the user',
        },
    ];

    const handleButtonClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowDropdown((prev) => !prev);
    };

    const getSourcesDisplayText = () => {
        return `Data Sources (${selectedSources.length})`;
    };

    const handleDropdownClose = useCallback(() => {
        if (!showDropdown) return;
        setShowDropdown(false);
        onClose();
    }, [showDropdown, onClose]);

    // Close on outside pointer down (tap/click)
    useEffect(() => {
        if (!showDropdown) return;
        const handlePointerDown = (event: PointerEvent) => {
            const root = selectorRef.current;
            const target = event.target as EventTarget | null;
            if (!root) return;
            if (!(target instanceof Node)) {
                handleDropdownClose();
                return;
            }
            if (root.contains(target)) return;
            handleDropdownClose();
        };
        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, [showDropdown, handleDropdownClose]);

    // Close on Escape key
    useEffect(() => {
        if (!showDropdown) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') handleDropdownClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showDropdown, handleDropdownClose]);

    const handleSourceToggle = (sourceValue: string) => {
        // Find the source to check if it's disabled
        const source = dataSources.find((s) => s.value === sourceValue);
        if (source?.isDisabled) {
            return; // Don't allow toggling disabled sources
        }

        const isCurrentlySelected = selectedSources.includes(sourceValue);

        if (isCurrentlySelected) {
            // Prevent deselecting if this is the last selected source
            if (selectedSources.length <= 1) {
                return; // Don't allow deselecting the last remaining source
            }
            // Remove from selection
            const newSources = selectedSources.filter((source) => source !== sourceValue);
            onSelectionChange(newSources);
        } else {
            // Add to selection
            const newSources = [...selectedSources, sourceValue];
            onSelectionChange(newSources);
        }
    };

    const activeDataSources = dataSources.filter((source) => source.isActive);

    return (
        <div className={styles.DataSourceSelectorWrapper} ref={selectorRef}>
            {/* Data Source Button */}
            <button
                className={`${styles.DataSourceButton} ${styles[buttonStyle]} DataSourceButton`}
                onClick={handleButtonClick}
                aria-controls={dropdownId}
                aria-expanded={showDropdown}
            >
                <span className={styles.SourceIcon}>
                    <img src={databaseSearchIcon} alt="Database Search" />
                </span>
                <span className={styles.SourceText}>{getSourcesDisplayText()}</span>
            </button>

            {/* Dropdown */}
            {showDropdown && (
                <div id={dropdownId} className={`${styles.DataSourceSelector} ${openAbove ? styles.OpenAbove : ''}`}>
                    <div className={styles.SourcesList}>
                        {activeDataSources.map((source) => {
                            const isSelected = selectedSources.includes(source.value);
                            const isHovered = hoveredSource === source.value;
                            const isDisabled = source.isDisabled;

                            // Don't show purple state if disabled
                            const shouldUsePurple = !isDisabled && (isSelected || isHovered);

                            // Create icon with appropriate color
                            const iconElement = shouldUsePurple ? source.iconPurple : source.icon;

                            return (
                                <div
                                    key={source.value}
                                    className={`${styles.SourceItem} ${isSelected ? styles.Selected : ''} ${
                                        isDisabled ? styles.Disabled : ''
                                    }`}
                                    onClick={() => handleSourceToggle(source.value)}
                                    onMouseEnter={() => !isDisabled && setHoveredSource(source.value)}
                                    onMouseLeave={() => !isDisabled && setHoveredSource(null)}
                                >
                                    <div className={styles.SourceInfo}>
                                        <div className={styles.SourceInfoLeft}>
                                            <div className={styles.SourceIcon}>{iconElement}</div>
                                            <div className={styles.SourceLabel}>{source.label}</div>
                                        </div>
                                        {isSelected && <TickIcon color="#6021B3" />}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataSourceSelector;
